<template>
	<div class="whatsapp-template-field col-lg-12 pa-0'" style="padding: 0 !important;">
		<!-- Template Selection -->
		<v-autocomplete
			class="col-12"
			v-model="localTemplateId"
			:items="templates"
			item-text="text"
			item-value="value"
			:label="label"
			:placeholder="placeholder"
			outlined
			dense
			:loading="loadingTemplates"
			:rules="required ? [v => !!v || 'Template richiesto'] : []"
			:prepend-icon="prependIcon"
			clearable
			@change="onTemplateChange"
		>
			<template v-slot:item="{ item }">
				<v-list-item-content>
					<v-list-item-title>{{ item.text }}</v-list-item-title>
					<v-list-item-subtitle>{{ item.body | truncate(80) }}</v-list-item-subtitle>
				</v-list-item-content>
			</template>
		</v-autocomplete>

		<!-- Template Name -->
		<v-subheader v-if="selectedTemplate" class="px-0 font-weight-bold">
			<v-icon small left color="primary">mdi-file-document</v-icon>
			{{ selectedTemplate.text }}
		</v-subheader>

		<!-- Template Preview -->
		<v-card v-if="selectedTemplate && showPreview" outlined class="mb-4 background-1">
			<v-card-subtitle>
				<v-icon small left>mdi-eye</v-icon>
				ANTEPRIMA MESSAGGIO
			</v-card-subtitle>
			<v-divider></v-divider>
			<v-card-text>
				<div class="text-body-2 white-space-pre-wrap">{{ selectedTemplate.body }}</div>
				<!-- Media preview if template has media (after body text) -->
				<div v-if="templateMedia && templateMedia.length > 0" class="mt-3">
					<v-img
						v-for="(mediaUrl, index) in templateMedia"
						:key="index"
						:src="mediaUrl"
						max-width="300"
						class="rounded mb-2"
					></v-img>
				</div>
			</v-card-text>
		</v-card>

		<!-- Variables Mapping Section -->
		<v-card v-if="selectedTemplate && templateVariables.length > 0 && showVariablesMapping" outlined class="mb-4 background-1">
			<v-card-subtitle>
				<v-icon small left>mdi-variable</v-icon>
				MAPPING VARIABILI
			</v-card-subtitle>
			<v-divider></v-divider>
			<v-card-text>
				<div class="text-caption mb-3 text--secondary">
					Mappa le variabili numeriche del template alle variabili del sistema o inserisci testo libero
				</div>

				<v-row v-for="varNum in templateVariables" :key="varNum" class="mb-2 align-center">
					<v-col cols="12" md="2" class="d-flex align-center">
						<v-chip small color="primary" outlined>
							<v-icon small left>mdi-numeric-{{ varNum }}-box</v-icon>
							<span v-text="formatVariableName(varNum)"></span>
						</v-chip>
						<v-icon small class="mx-2">mdi-arrow-right</v-icon>
					</v-col>
					<v-col cols="12" md="5">
						<v-autocomplete
							:value="getVariableValue(varNum)"
							@input="setVariableValue(varNum, $event)"
							:items="availableVariables"
							item-text="label"
							item-value="value"
							placeholder="Seleziona variabile (opzionale)"
							outlined
							dense
							clearable
							prepend-icon="mdi-code-braces"
						>
							<template v-slot:selection="{ item }">
								<v-chip small>
									<v-icon small left>mdi-code-braces</v-icon>
									{{ item.label }}
								</v-chip>
							</template>

							<template v-slot:item="{ item }">
								<v-list-item-content>
									<v-list-item-title>
										<v-chip x-small outlined class="mr-2">{{ item.category }}</v-chip>
										{{ item.label }}
									</v-list-item-title>
									<v-list-item-subtitle class="text-caption">
										{{ item.value }}
									</v-list-item-subtitle>
								</v-list-item-content>
							</template>
						</v-autocomplete>
					</v-col>
					<v-col cols="12" md="5">
						<v-text-field
							:value="getFallbackValue(varNum)"
							@input="setFallbackValue(varNum, $event)"
							:placeholder="getVariableValue(varNum) ? 'Fallback se variabile vuota' : 'Testo libero'"
							outlined
							dense
							prepend-icon="mdi-format-text"
							:hint="getVariableValue(varNum) ? 'Usato se la variabile è vuota' : 'Testo fisso da inserire'"
							persistent-hint
						/>
					</v-col>
				</v-row>
			</v-card-text>
		</v-card>
	</div>
</template>

<script>
	export default {
		name: 'WhatsAppTemplateField',
		props: {
			value: {
				type: [Object, Number, String],
				default: null
			},
			label: {
				type: String,
				default: 'Template WhatsApp'
			},
			placeholder: {
				type: String,
				default: 'Seleziona un template WhatsApp'
			},
			prependIcon: {
				type: String,
				default: null
			},
			required: {
				type: Boolean,
				default: false
			},
			showPreview: {
				type: Boolean,
				default: true
			},
			showVariablesMapping: {
				type: Boolean,
				default: true
			}
		},

		data() {
			// Determina se value è un oggetto o un semplice ID
			const isObject = typeof this.value === 'object' && this.value !== null

			// Estrai template_id e variables_mapping in base al tipo di value
			const templateId = isObject ? this.value.template_id : this.value
			let variablesMapping = isObject ? (this.value.variables_mapping || {}) : {}

			// Fix: se è un array, convertilo in oggetto con chiavi numeriche partendo da 1
			if (Array.isArray(variablesMapping)) {
				const mappingObject = {}
				variablesMapping.forEach((value, index) => {
					mappingObject[String(index + 1)] = value
				})
				variablesMapping = mappingObject
			}

			return {
				localTemplateId: templateId || null,
				localVariablesMapping: { ...variablesMapping },
				templates: [],
				loadingTemplates: false,
				selectedTemplate: null,
				availableVariables: []
			}
		},

		computed: {
			parsedOptions() {
				if (!this.selectedTemplate || !this.selectedTemplate.options) return null

				if (typeof this.selectedTemplate.options === 'string') {
					try {
						return JSON.parse(this.selectedTemplate.options)
					} catch (e) {
						return null
					}
				}
				return this.selectedTemplate.options
			},

			templateMedia() {
				if (!this.selectedTemplate) return []

				const options = this.parsedOptions
				if (!options) return []

				// Check if template has media in options
				if (options.media && Array.isArray(options.media)) {
					return options.media
				}

				// Check in twilio_content_json
				if (options.twilio_content_json && options.twilio_content_json.types) {
					const types = options.twilio_content_json.types
					// Check twilio/media type
					if (types['twilio/media'] && types['twilio/media'].media) {
						return types['twilio/media'].media
					}
				}

				return []
			},

			templateVariables() {
				if (!this.selectedTemplate || !this.selectedTemplate.body) return []

				// Estrai variabili numeriche dal body: {{1}}, {{2}}, {{3}}, ecc.
				const matches = this.selectedTemplate.body.match(/\{\{(\d+)\}\}/g)
				if (!matches) return []

				// Estrai i numeri e rimuovi duplicati
				const numbers = matches.map(m => m.replace(/\{\{|\}\}/g, ''))
				return [...new Set(numbers)].sort((a, b) => parseInt(a) - parseInt(b))
			}
		},

		watch: {
			value: {
				handler(newValue) {
					const isObject = typeof newValue === 'object' && newValue !== null
					const templateId = isObject ? newValue.template_id : newValue

					if (templateId !== this.localTemplateId) {
						this.localTemplateId = templateId
						if (this.localTemplateId && this.templates.length > 0) {
							this.selectedTemplate = this.templates.find(t => t.value === this.localTemplateId)
						}
					}

					if (isObject && newValue.variables_mapping !== this.localVariablesMapping) {
						this.localVariablesMapping = { ...newValue.variables_mapping }
					}
				},
				deep: true
			}
		},

		mounted() {
			this.loadTemplates()
			this.loadAvailableVariables()
		},

		methods: {
			loadTemplates() {
				this.loadingTemplates = true

				this.axios.get('get-whatsapp-templates').then(response => {

					this.templates = response.data.data.templates || []
					this.loadingTemplates = false

					// Se c'è già un template selezionato, caricalo
					if (this.localTemplateId) {
						this.selectedTemplate = this.templates.find(t => t.value === this.localTemplateId)

						// Inizializza il mapping con i valori esistenti se presenti
						this.$nextTick(() => {
							this.initializeVariablesMapping()
						})
					}
				}).catch(error => {
					console.error('Error loading WhatsApp templates:', error)
					this.loadingTemplates = false
					this.$store.commit('setSnackbar', {
						timeout: 6000,
						text: 'Errore nel caricamento dei template WhatsApp',
						value: true,
						color: 'error'
					})
				})
			},

			loadAvailableVariables() {
				// Lista delle variabili disponibili nel sistema
				// Queste corrispondono alle variabili supportate da parseTemplate()
				this.availableVariables = [
					{ category: 'Azienda', label: 'Nome Azienda', value: '{{AZIENDA_NOME}}' },
					{ category: 'Azienda', label: 'Telefono Primario', value: '{{AZIENDA_TELEFONO_PRIMARIO}}' },
					{ category: 'Azienda', label: 'Telefono Secondario', value: '{{AZIENDA_TELEFONO_SECONDARIO}}' },
					{ category: 'Azienda', label: 'Email Primaria', value: '{{AZIENDA_EMAIL_PRIMARIA}}' },
					{ category: 'Azienda', label: 'Email Secondaria', value: '{{AZIENDA_EMAIL_SECONDARIA}}' },
					{ category: 'Azienda', label: 'Email PEC', value: '{{AZIENDA_EMAIL_PEC}}' },
					{ category: 'Azienda', label: 'Via', value: '{{AZIENDA_VIA}}' },
					{ category: 'Azienda', label: 'Numero Civico', value: '{{AZIENDA_NUMERO_CIVICO}}' },
					{ category: 'Azienda', label: 'CAP', value: '{{AZIENDA_COD_POSTALE}}' },
					{ category: 'Azienda', label: 'Città', value: '{{AZIENDA_CITTA}}' },
					{ category: 'Azienda', label: 'Provincia', value: '{{AZIENDA_PROVINCIA}}' },
					{ category: 'Azienda', label: 'IBAN', value: '{{AZIENDA_IBAN}}' },
					{ category: 'Azienda', label: 'P.IVA', value: '{{AZIENDA_PIVA}}' },
					{ category: 'Azienda', label: 'Codice Fiscale', value: '{{AZIENDA_CODFISCALE}}' },

					{ category: 'Preventivo', label: 'Data Preventivo', value: '{{PREVENTIVO_DATA}}' },
					{ category: 'Preventivo', label: 'Totale Preventivo', value: '{{PREVENTIVO_TOTALE}}' },
					{ category: 'Preventivo', label: 'Imponibile Preventivo', value: '{{PREVENTIVO_IMPONIBILE}}' },
					{ category: 'Preventivo', label: 'IVA Preventivo', value: '{{PREVENTIVO_IVA}}' },
					{ category: 'Preventivo', label: 'Link Firma', value: '{{LINK_FIRMA}}' },

					{ category: 'Fattura', label: 'Data Documento', value: '{{DOCUMENTO_DATA}}' },
					{ category: 'Fattura', label: 'Numero Documento', value: '{{DOCUMENTO_NUMERO}}' },
					{ category: 'Fattura', label: 'Sezionale Documento', value: '{{DOCUMENTO_SEZIONALE}}' },
					{ category: 'Fattura', label: 'Totale Documento', value: '{{DOCUMENTO_TOTALE}}' },
					{ category: 'Fattura', label: 'Imponibile Documento', value: '{{DOCUMENTO_IMPONIBILE}}' },
					{ category: 'Fattura', label: 'IVA Documento', value: '{{DOCUMENTO_IVA}}' },

					{ category: 'DDT', label: 'Data DDT', value: '{{DDT_DATA}}' },
					{ category: 'DDT', label: 'Numero DDT', value: '{{DDT_NUMERO}}' },
					{ category: 'DDT', label: 'Sezionale DDT', value: '{{DDT_SEZIONALE}}' },
					{ category: 'DDT', label: 'Totale DDT', value: '{{DDT_TOTALE}}' },
					{ category: 'DDT', label: 'Imponibile DDT', value: '{{DDT_IMPONIBILE}}' },
					{ category: 'DDT', label: 'IVA DDT', value: '{{DDT_IVA}}' },

					{ category: 'Contatto', label: 'Nome Contatto', value: '{{CONTATTO_NOME}}' },

					{ category: 'Asset', label: 'Nome Asset', value: '{{ASSET_NOME}}' },

					{ category: 'Prodotto', label: 'Nome Prodotto', value: '{{PRODOTTO_NOME}}' },
					{ category: 'Prodotto', label: 'Data Scheda', value: '{{SCHEDA_DATA}}' },
					{ category: 'Prodotto', label: 'Revisione Scheda', value: '{{SCHEDA_REVISIONE}}' },

					{ category: 'Contratto', label: 'Nome Contratto', value: '{{CONTRATTO_NOME}}' },

					{ category: 'Chiamata', label: 'Numero Chiamata', value: '{{CHIAMATA_NUMERO}}' },
					{ category: 'Chiamata', label: 'Titolo Chiamata', value: '{{CHIAMATA_TITOLO}}' },
					{ category: 'Chiamata', label: 'Data Chiamata', value: '{{CHIAMATA_DATA}}' },
					{ category: 'Chiamata', label: 'Data Inizio Chiamata', value: '{{CHIAMATA_DATA_INIZIO}}' },
					{ category: 'Chiamata', label: 'Richiesta Chiamata', value: '{{CHIAMATA_RICHIESTA}}' },
					{ category: 'Chiamata', label: 'Stato Chiamata', value: '{{CHIAMATA_STATO}}' },

					{ category: 'Scadenza', label: 'Sezionale Fattura', value: '{{SEZIONALE_FATTURA}}' },
					{ category: 'Scadenza', label: 'Numero Fattura', value: '{{NUMERO_FATTURA}}' },
					{ category: 'Scadenza', label: 'Data Fattura', value: '{{DATA_FATTURA}}' },
					{ category: 'Scadenza', label: 'Totale Fattura', value: '{{TOTALE_FATTURA}}' },
					{ category: 'Scadenza', label: 'Condizioni Pagamento', value: '{{CONDIZIONI_PAGAMENTO}}' },
					{ category: 'Scadenza', label: 'Data Scadenza', value: '{{DATA_SCADENZA}}' },
					{ category: 'Scadenza', label: 'Importo Scadenza', value: '{{IMPORTO_SCADENZA}}' },

					{ category: 'Generali', label: 'Username', value: '{{USERNAME}}' },
					{ category: 'Generali', label: 'Anno Corrente', value: '{{ANNO_CORRENTE}}' },
					{ category: 'Generali', label: 'Data Oggi', value: '{{DATA_OGGI}}' }
				]
			},

			onTemplateChange(templateId) {
				if (templateId) {
					this.selectedTemplate = this.templates.find(t => t.value === templateId)

					// Reset variables mapping quando si cambia template usando $set
					this.$set(this, 'localVariablesMapping', {})
				} else {
					this.selectedTemplate = null
					this.$set(this, 'localVariablesMapping', {})
				}

				this.emitUpdate()
			},

			/**
			 * Recupera il valore della variabile di sistema per una posizione
			 * Supporta sia formato legacy (stringa) che nuovo formato (oggetto con variable/fallback)
			 */
			getVariableValue(varNum) {
				const mapping = this.localVariablesMapping?.[varNum]
				if (!mapping) return null

				// Nuovo formato: { variable: "{{...}}", fallback: "testo" }
				if (typeof mapping === 'object' && mapping !== null) {
					return mapping.variable || null
				}

				// Formato legacy: stringa diretta (es. "{{AZIENDA_NOME}}")
				return mapping
			},

			/**
			 * Recupera il valore fallback/testo libero per una posizione
			 */
			getFallbackValue(varNum) {
				const mapping = this.localVariablesMapping?.[varNum]
				if (!mapping) return ''

				// Nuovo formato: { variable: "{{...}}", fallback: "testo" }
				if (typeof mapping === 'object' && mapping !== null) {
					return mapping.fallback || ''
				}

				// Formato legacy: non c'è fallback
				return ''
			},

			/**
			 * Imposta il valore della variabile di sistema per una posizione
			 */
			setVariableValue(varNum, value) {
				const currentMapping = this.localVariablesMapping?.[varNum] || {}
				const fallback = typeof currentMapping === 'object' ? (currentMapping.fallback || '') : ''

				// Crea nuovo oggetto con variable e fallback
				this.$set(this.localVariablesMapping, varNum, {
					variable: value || null,
					fallback: fallback
				})

				this.emitUpdate()
			},

			/**
			 * Imposta il valore fallback/testo libero per una posizione
			 */
			setFallbackValue(varNum, value) {
				const currentMapping = this.localVariablesMapping?.[varNum] || {}

				// Determina il valore variable attuale
				let variable = null
				if (typeof currentMapping === 'object' && currentMapping !== null) {
					variable = currentMapping.variable || null
				} else if (typeof currentMapping === 'string') {
					// Conversione da formato legacy
					variable = currentMapping
				}

				// Crea nuovo oggetto con variable e fallback
				this.$set(this.localVariablesMapping, varNum, {
					variable: variable,
					fallback: value || ''
				})

				this.emitUpdate()
			},

			initializeVariablesMapping() {
				// Assicura che tutte le chiavi del mapping esistano come proprietà reattive
				if (!this.localVariablesMapping) {
					this.$set(this, 'localVariablesMapping', {})
					return
				}

				// Se esiste già un mapping (edit mode), assicura che sia reattivo
				// e converte formato legacy al nuovo formato
				const existingMapping = { ...this.localVariablesMapping }
				this.$set(this, 'localVariablesMapping', {})

				// Ricrea il mapping usando $set per ogni chiave
				Object.keys(existingMapping).forEach(key => {
					const value = existingMapping[key]

					// Converti formato legacy (stringa) al nuovo formato (oggetto)
					if (typeof value === 'string') {
						this.$set(this.localVariablesMapping, key, {
							variable: value,
							fallback: ''
						})
					} else {
						// Già nel nuovo formato
						this.$set(this.localVariablesMapping, key, value)
					}
				})
			},

			formatVariableName(varNum) {
				return '{{' + varNum + '}}'
			},

			emitUpdate() {
				// Se showVariablesMapping è false, emetti solo l'ID
				// Altrimenti emetti l'oggetto completo
				if (!this.showVariablesMapping) {
					this.$emit('input', this.localTemplateId)
				} else {
					const updated = {
						template_id: this.localTemplateId,
						variables_mapping: { ...this.localVariablesMapping }
					}
					this.$emit('input', updated)
				}
			}
		},

		filters: {
			truncate(text, length = 100) {
				if (!text) return ''
				if (text.length <= length) return text
				return text.substring(0, length) + '...'
			}
		}
	}
</script>

<style scoped>
	.whatsapp-template-field {
		width: 100%
	}

	.white-space-pre-wrap {
		white-space: pre-wrap
	}
</style>
